import { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { InlineLoader } from "@/components/LoadingSkeleton";
import {
  ChartBar, Queue, CalendarBlank, Syringe, List, TrendUp,
  Bell, User, SignOut, Ticket,
  FileText, GearSix, Users, UploadSimple, WarningCircle,
} from "@phosphor-icons/react";

const SIDEBAR_ITEMS = [
  { to: "/doctor", label: "Dashboard", icon: ChartBar, end: true },
  { to: "/doctor/treatment-plans", label: "Treatment Plans", icon: FileText },
  { to: "/doctor/procedure-bookings", label: "Procedure Bookings", icon: Syringe },
  { to: "/doctor/manage-procedures", label: "Manage Procedures", icon: List },
  { to: "/doctor/future-appointments", label: "Future Appointments", icon: CalendarBlank },
  { to: "/doctor/patients", label: "Patients", icon: Users },
  { to: "/doctor/reschedule-center", label: "Reschedule", icon: WarningCircle },
  { to: "/doctor/import-patients", label: "Import Patients", icon: UploadSimple },
  { to: "/analytics/revenue", label: "Analytics", icon: TrendUp },
];

export default function DoctorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [clinicName, setClinicName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const profileRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!user || user.role !== "doctor") return;
    api.get("/doctors/me").then(({ data }) => {
      if (data?.clinic_name) setClinicName(data.clinic_name);
      if (data?.specialty) setSpecialty(data.specialty);
      if (data?.subscription) setSubscriptionInfo(data.subscription);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications/inbox/unread-count");
        setUnreadCount(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const openNotifPanel = async () => {
    if (notifOpen) { setNotifOpen(false); return; }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const { data } = await api.get("/notifications/inbox?limit=5");
      setNotifications(data.notifications || []);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const markNotifRead = async (id) => {
    try {
      await api.patch("/notifications/inbox/read", { notification_id: id });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/notifications/inbox/read", { all: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate("/", { replace: true });
  };
  const sidebarExpanded = sidebarOpen || sidebarHover;
  const isActiveTrial =
    subscriptionInfo?.days_remaining != null
    && subscriptionInfo.days_remaining > 0
    && subscriptionInfo.subscription_status === "trial"
    && subscriptionInfo.subscription_plan === "trial";

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        onMouseEnter={() => setSidebarHover(true)}
        onMouseLeave={() => setSidebarHover(false)}
        className={`fixed inset-y-0 left-0 z-40 overflow-hidden bg-card border-r border-border transform transition-[width,transform] duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto ${sidebarExpanded ? "w-64" : "w-16"} ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center h-16 border-b border-border gap-2 px-4">
          <Link to="/doctor" className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-display font-bold text-sm shrink-0">
            {clinicName ? clinicName.charAt(0).toUpperCase() : "M"}
          </Link>
          <Link
            to="/doctor"
            className={`font-display font-bold text-lg text-foreground min-w-0 leading-tight whitespace-nowrap overflow-hidden transition-all duration-200 ease-out ${
              sidebarExpanded ? "max-w-[160px] opacity-100 translate-x-0 delay-75" : "max-w-0 opacity-0 -translate-x-2"
            }`}
            title={clinicName || "My Clinic"}
          >
              {clinicName || "My Clinic"}
          </Link>
        </div>
        <nav className="p-2 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {SIDEBAR_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => { setSidebarOpen(false); }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
                title={!sidebarExpanded ? item.label : undefined}
              >
                <Icon size={20} weight="duotone" className="shrink-0" />
                <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ease-out ${
                  sidebarExpanded ? "max-w-[180px] opacity-100 translate-x-0 delay-75" : "max-w-0 opacity-0 -translate-x-2"
                }`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top navbar */}
        <header className="h-16 border-b border-border bg-card flex items-center gap-4 px-4 lg:px-6 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-muted text-muted-foreground">
            <List size={22} />
          </button>
          {/* Top nav links */}
          <nav className="hidden sm:flex items-center gap-1">
            <NavLink
              to="/doctor/queue"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <Queue size={16} weight="duotone" /> Today's Queue
            </NavLink>
            <NavLink
              to="/doctor/appointments"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <CalendarBlank size={16} weight="duotone" /> All Appointments
            </NavLink>
          </nav>

          {/* Trial remaining badge */}
          {isActiveTrial && (
            <Link
              to="/plan-details"
              className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                subscriptionInfo.days_remaining <= 7
                  ? "border-red-200 bg-red-50 text-red-700"
                  : subscriptionInfo.days_remaining <= 30
                    ? "border-yellow-200 bg-yellow-50 text-yellow-700"
                    : "border-green-200 bg-green-50 text-green-700"
              }`}
            >
              {subscriptionInfo.days_remaining}d left
            </Link>
          )}

          <div className="flex-1" />

          {/* Notif bell */}
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              onClick={openNotifPanel}
              className="relative p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Notifications"
            >
              <Bell size={18} weight="duotone" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-border bg-card shadow-lg z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="text-sm font-semibold text-foreground">Notifications</span>
                  {unreadCount > 0 && (
                    <button type="button" onClick={markAllRead} className="text-xs text-primary font-semibold hover:underline">
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifLoading ? (
                    <InlineLoader message="Loading notifications" className="m-3" />
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-xs text-muted-foreground">No notifications yet</div>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => { if (!n.read) markNotifRead(n.id); }}
                        className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors flex items-start gap-3 ${n.read ? "" : "bg-primary/[0.03]"}`}
                      >
                        {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                        {n.read && <span className="w-2 h-2 shrink-0 mt-1.5" />}
                        <div className="min-w-0 flex-1">
                          <div className={`text-xs ${n.read ? "text-foreground" : "text-foreground font-semibold"}`}>{n.title}</div>
                          <div className="text-[11px] text-muted-foreground truncate mt-0.5">{n.body}</div>
                          <div className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(n.created_at)}</div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <Link to="/notifications" onClick={() => setNotifOpen(false)} className="block px-4 py-3 text-center text-xs font-semibold text-primary border-t border-border hover:bg-muted/50 transition-colors">
                  View all notifications
                </Link>
              </div>
            )}
          </div>

          {/* Profile dropdown */}
          <div className="relative ml-1" ref={profileRef}>
            <button
              type="button"
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 rounded-full p-1 hover:bg-muted transition-colors"
            >
              <div className="hidden sm:block text-right min-w-0 max-w-[140px]">
                <div className="text-sm font-semibold text-foreground truncate">{user?.name || "Doctor"}</div>
                {specialty && <div className="text-[11px] text-muted-foreground truncate">{specialty}</div>}
              </div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {user?.photo_url ? (
                  <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User size={18} weight="duotone" className="text-primary" />
                )}
              </div>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-muted-foreground hidden sm:block">
                <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {profileOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-border bg-card shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-border">
                  <div className="text-sm font-semibold text-foreground truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-widest text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Doctor</span>
                </div>
                <Link to="/doctor/account-settings" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                  <GearSix size={16} weight="duotone" /> Account & Settings
                </Link>
                <Link to="/support" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                  <Ticket size={16} weight="duotone" /> Support Tickets
                </Link>
                <div className="border-t border-border mx-2 my-1" />
                <button type="button" onClick={() => { setProfileOpen(false); setShowLogoutDialog(true); }} className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors">
                  <SignOut size={16} weight="duotone" /> Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto px-4 pb-4 pt-2 lg:px-6 lg:pb-6 lg:pt-3">
          <Outlet />
        </main>
      </div>

      {/* Logout confirmation */}
      {showLogoutDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xs rounded-2xl bg-card border border-border p-6 shadow-xl">
            <p className="text-center text-sm font-semibold text-foreground">Are you sure you want to log out?</p>
            <div className="mt-5 flex items-center gap-3">
              <button type="button" onClick={() => setShowLogoutDialog(false)} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                No
              </button>
              <button type="button" onClick={handleLogout} disabled={loggingOut} className="flex-1 rounded-full bg-destructive text-destructive-foreground py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                {loggingOut ? "Signing out…" : "Yes, log out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
